import { Button } from "components/ui/button";
import { toast } from "components/toast";
import { DownloadCloudIcon } from "lucide-react";
import { TaskBase } from "pages/project/common/taskBase.typing";
import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHubConnection } from "store/useHubConnection";
import { LanguageType } from "utils/language";

interface Props<TaskBaseType extends TaskBase> {
  selectedTaskBase: TaskBaseType;
  disableDownload: boolean;
  onRequestZipFullDownload: (
    selectedTaskBase: TaskBaseType,
    currentLanguage: LanguageType,
    hubConnectionId: string
  ) => unknown;
  /** Lets Task/Offer gate the download behind the mandatory instructions confirmation. */
  onBeforeDownload?: (download: () => void) => void;
}

function TaskBaseFilesDownload<TaskBaseType extends TaskBase>({
  selectedTaskBase,
  ...props
}: Props<TaskBaseType>): ReactElement {
  const ensureConnectionId = useHubConnection((state) => state.ensureConnectionId);
  const connection = useHubConnection((state) => state.connection);
  const connectionState = useHubConnection((state) => state.connectionState);
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language as LanguageType;
  const [isEnsuringConnection, setIsEnsuringConnection] = useState(false);

  const handleOnDownload = async () => {
    if (!selectedTaskBase || isEnsuringConnection) {
      return;
    }

    const isAlreadyConnected = connectionState === "Connected" && Boolean(connection?.connectionId);
    let reconnectToastId: string | undefined;

    setIsEnsuringConnection(true);
    try {
      if (!isAlreadyConnected) {
        reconnectToastId = toast.loading(t("translation:fileDownload.preparingDownload"));
      }

      const connectionId = await ensureConnectionId();
      if (reconnectToastId) {
        toast.dismiss(reconnectToastId);
      }

      if (!connectionId) {
        toast.error(t("translation:common.hub.failedToConnect"));
        return;
      }

      props.onRequestZipFullDownload(selectedTaskBase, currentLanguage, connectionId);
    } finally {
      if (reconnectToastId) {
        toast.dismiss(reconnectToastId);
      }
      setIsEnsuringConnection(false);
    }
  };

  const handleOnClick = () => (props.onBeforeDownload ? props.onBeforeDownload(handleOnDownload) : handleOnDownload());

  return (
    <Button
      variant="outline"
      disabled={props.disableDownload || isEnsuringConnection}
      data-test={`btn-download-file-${selectedTaskBase.id}`}
      data-dd-action-name="btn-download-file"
      className="flex w-full flex-nowrap gap-2 text-nowrap"
      onClick={handleOnClick}
    >
      <DownloadCloudIcon className="size-5 shrink-0" data-icon="CloudDownloadIcon" />
      <span>{t("translation:fileDownload.link")}</span>
    </Button>
  );
}

export default TaskBaseFilesDownload;
